import React from 'react';
import { Youtube, Facebook, Twitch } from 'lucide-react';
import { FaDiscord, FaInstagram, FaPatreon } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord, faInstagram, faPatreon } from '@fortawesome/free-brands-svg-icons';

// Custom icons with brand colors
const TikTokIcon = () => (
  <svg 
    viewBox="0 0 24 24" 
    className="w-6 h-6"
  >
    <path d="M16.6 5.82c.9 1.04 2.19 1.69 3.4 1.69V4.5c-.24 0-.48-.03-.71-.09v2.32c-1.21 0-2.5-.65-3.4-1.69v7.67c0 3.85-3.13 6.99-6.99 6.99-1.38 0-2.67-.4-3.75-1.09 1.22 1.23 2.91 2 4.78 2 3.86 0 6.99-3.14 6.99-6.99V5.82z" fill="#00f2ea"/>
    <path d="M16.6 4.5v1.32c-.91-1.05-1.5-2.4-1.5-3.82h-2.79c0 4.51 3.67 8.18 8.18 8.18v-2.79c-1.21 0-2.5-.65-3.4-1.69" fill="#00f2ea"/>
    <path d="M8.9 12.89c-2.01 0-3.64 1.63-3.64 3.64 0 2.01 1.63 3.64 3.64 3.64 2.01 0 3.64-1.63 3.64-3.64v-3.64H8.9z" fill="#ff0050"/>
  </svg>
);

const Instagram = () => (
    <FaInstagram size={25} />
);

const DiscordIcon = () => (
    <FontAwesomeIcon icon={faDiscord} size={"2x"} />
);

const PatreonIcon = () => (
    <FontAwesomeIcon icon={faPatreon} size="2x" />
);

const RedbubbleIcon = () => (
  <svg 
    viewBox="0 0 24 24" 
    className="w-7 h-7"
    fill="#E41321"
  >
    <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 16.5a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"/>
    <path d="M14.5 8.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z"/>
    <path d="M9.5 15.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z"/>
    <path d="M19.5 15.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z"/>
  </svg>
);

const SocialBar = ({ id }) => {
    const socialLinks = [
    { 
      name: 'TikTok',
      Icon: TikTokIcon,
      url: 'https://www.tiktok.com/@jorgeherrans',
      label: '@epicmusical',
      hoverColor: 'hover:text-[#00f2ea]'
    },
    { 
      name: 'YouTube',
      Icon: Youtube,
      url: 'https://www.youtube.com/channel/UCvayv7u6HrpENlleXekJ2fg',
      label: 'Jorge Rivera-Herrans',
      hoverColor: 'hover:text-[#FF0000]',
      iconColor: '#FF0000'
    },
    { 
      name: 'Discord',
      Icon: DiscordIcon,
      url: 'https://discord.com/epicthemusical',
      label: 'Join Community',
      hoverColor: 'hover:text-[#5865F2]'
    },
    { 
      name: 'Twitch',
      Icon: Twitch,
      url: 'https://www.twitch.tv/jayherrans',
      label: 'Jorge\'s Streaming Series',
      hoverColor: 'hover:text-[#9146FF]',
      iconColor: '#9146FF'
    },
    { 
      name: 'Instagram',
      Icon: Instagram,
      url: 'https://www.instagram.com/jayherrans/?hl=en',
      label: 'Highlights and Exclusive Content',
      hoverColor: 'hover:text-[#1877F2]',
      iconColor: '#1877F2'
    },
    { 
      name: 'Facebook',
      Icon: Facebook,
      url: 'https://www.facebook.com/jayherrans/',
      label: 'Exclusive Content and Highlights',
      hoverColor: 'hover:text-[#1877F2]',
      iconColor: '#1877F2'
    },
    { 
      name: 'Redbubble',
      Icon: RedbubbleIcon,
      url: 'https://www.redbubble.com/people/epicthemusical/shop?asc=u',
      label: 'Official Merch',
      hoverColor: 'hover:text-[#E41321]'
    },
    { 
      name: 'Patreon',
      Icon: PatreonIcon,
      url: 'https://www.patreon.com/jayherrans',
      label: 'Early Access and BTS',
      hoverColor: 'hover:text-[#FF424D]'
    }
  ];

  return (
    <div id={id} className="w-full bg-black/80 backdrop-blur-sm border-t border-b border-white/10">
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8">
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.url}
              className="flex flex-col items-center justify-center text-center group"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.name}
            >
              <div className="mb-3 relative">
                <social.Icon 
                  className="w-6 h-6" 
                  color={social.iconColor || 'currentColor'}
                />
                <div className="absolute -inset-2 border border-white/0 group-hover:border-white/20 transition-all duration-300 rounded-lg" />
              </div>
              
              <span className="text-xs text-white/70 group-hover:text-white tracking-wider uppercase transition-colors duration-300 min-h-[2rem] flex items-center text-center">
                {social.label}
              </span>
            </a>
          ))}
        </div>
      </div>
      
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/20 to-transparent" />
    </div>
  );
};

export default SocialBar;